import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Wrench,
  UserCog,
  Wallet,
  Package,
  CarFront,
  CalendarClock,
  FileText,
  Layers,
  Store,
  ShieldCheck,
  Landmark,
  CalendarDays,
  Menu,
  X,
  LogOut,
  KeyRound,
  Settings,
} from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Özet", icon: LayoutDashboard, testId: "nav-dashboard", end: true },
  { to: "/periods", label: "Geçmiş Dönemler", icon: CalendarClock, testId: "nav-periods" },
  { to: "/jobs", label: "İş Kayıtları", icon: Wrench, testId: "nav-jobs" },
  { to: "/insurance", label: "Sigorta İşleri", icon: ShieldCheck, testId: "nav-insurance" },
  { to: "/quotes", label: "Teklifler", icon: FileText, testId: "nav-quotes" },
  { to: "/parts", label: "Parça Fiyat Listesi", icon: Layers, testId: "nav-parts" },
  { to: "/employees", label: "Çalışanlar", icon: UserCog, testId: "nav-employees" },
  { to: "/salaries", label: "Maaş Ödemeleri", icon: Wallet, testId: "nav-salaries" },
  { to: "/supplies", label: "Malzeme Giderleri", icon: Package, testId: "nav-supplies" },
  { to: "/shop-expenses", label: "Dükkan Giderleri", icon: Store, testId: "nav-shop-expenses" },
  { to: "/finans", label: "Finans", icon: Landmark, testId: "nav-finans" },
  { to: "/appointments", label: "Randevular", icon: CalendarDays, testId: "nav-appointments" },
];

const SidebarBody = ({ onNavigate }) => (
  <>
    <div className="px-6 py-6 border-b border-slate-800">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
          <CarFront className="w-5 h-5 text-white" strokeWidth={2.2} />
        </div>
        <div>
          <p className="font-display font-bold text-base leading-tight text-white">Global Car</p>
          <p className="text-[11px] uppercase tracking-widest text-slate-400">Service · Muhasebe</p>
        </div>
      </div>
    </div>

    <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
      <p className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Menü</p>
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={item.testId}
          onClick={onNavigate}
          className={({ isActive }) =>
            `nav-item flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium ${
              isActive ? "active bg-slate-800/70 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/40"
            }`
          }
        >
          <item.icon className="w-4 h-4" strokeWidth={2} />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>

    <div className="px-4 py-4 border-t border-slate-800">
      <div className="rounded-lg bg-slate-900 border border-slate-800 p-3 flex items-start gap-2">
        <CalendarClock className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
        <div className="text-[11px] leading-snug text-slate-400">
          <p className="text-slate-200 font-semibold">Haftalık Maaş Dönemi</p>
          <p>Pazartesi başlangıçlı hafta bazlı takip.</p>
        </div>
      </div>
    </div>
  </>
);

const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (next.length < 6) {
      toast.error("Yeni şifre en az 6 karakter olmalı");
      return;
    }
    if (next !== confirm) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Şifre değiştirildi");
      onOpenChange(false);
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Değiştirilemedi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Şifre Değiştir</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Mevcut Şifre</Label>
            <Input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} />
          </div>
          <div>
            <Label>Yeni Şifre</Label>
            <Input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <div>
            <Label>Yeni Şifre (Tekrar)</Label>
            <Input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button data-testid="cp-submit" onClick={submit} disabled={loading} className="bg-slate-900 hover:bg-slate-800">
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BusinessSettingsDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({
    business_name: "",
    phone: "",
    address: "",
    google_review_url: "",
    google_maps_url: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/business-settings");
        setForm({
          business_name: r.data.business_name || "",
          phone: r.data.phone || "",
          address: r.data.address || "",
          google_review_url: r.data.google_review_url || "",
          google_maps_url: r.data.google_maps_url || "",
        });
      } catch (e) {
        // ignore
      }
    })();
  }, [open]);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/business-settings", form);
      toast.success("İşletme ayarları kaydedildi");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="business-settings-dialog" className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Settings className="w-5 h-5" /> İşletme Ayarları
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>İşletme Adı</Label>
            <Input
              data-testid="bs-name-input"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              placeholder="Global Car Service"
            />
            <p className="text-[11px] text-slate-500 mt-1">Müşteri portalı ve WhatsApp mesajlarında gösterilir.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Telefon</Label>
              <Input
                data-testid="bs-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="0(212) 555 55 55"
              />
            </div>
            <div>
              <Label>Google Maps Linki</Label>
              <Input
                data-testid="bs-maps-input"
                value={form.google_maps_url}
                onChange={(e) => setForm({ ...form, google_maps_url: e.target.value })}
                placeholder="https://maps.app.goo.gl/..."
              />
            </div>
          </div>
          <div>
            <Label>Adres</Label>
            <Input
              data-testid="bs-address-input"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Örn: Sanayi Sitesi No: 123, İstanbul"
            />
          </div>
          <div>
            <Label>Google Yorum Linki</Label>
            <Input
              data-testid="bs-review-input"
              value={form.google_review_url}
              onChange={(e) => setForm({ ...form, google_review_url: e.target.value })}
              placeholder="https://g.page/r/XXXXXX/review"
            />
            <div className="text-[11px] text-slate-500 mt-1 space-y-1">
              <p>Müşteri portalında iş "Tamamlandı" olunca "Google'da Yorum Bırak" butonu çıkar.</p>
              <p>Linki almak için: Google Business Profile → Yorumlar → "İnceleme yaz linkini paylaş"</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button data-testid="save-business-settings" onClick={save} disabled={loading} className="bg-slate-900 hover:bg-slate-800">
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Header = ({ onOpenSidebar }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [cpOpen, setCpOpen] = useState(false);
  const [bsOpen, setBsOpen] = useState(false);
  const current = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));
  const title = current?.label || "Özet";

  const today = new Date().toLocaleDateString("tr-TR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <header data-testid="app-header" className="h-16 bg-white border-b border-slate-200 sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6 lg:px-8 gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden shrink-0"
          onClick={onOpenSidebar}
          data-testid="open-sidebar-btn"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <div className="min-w-0">
          <p className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 font-semibold truncate">
            Kaporta · Boya Atölyesi
          </p>
          <h1 className="font-display text-base sm:text-lg font-bold text-slate-900 leading-tight truncate">
            {title}
          </h1>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="hidden md:block text-right">
          <p className="text-xs text-slate-500">Bugün</p>
          <p className="text-sm font-semibold text-slate-800 capitalize">{today}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" className="w-9 h-9 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center hover:bg-slate-800">
              {(user?.name || user?.email || "A").slice(0, 1).toUpperCase()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-sm font-semibold">{user?.name || "Admin"}</div>
              <div className="text-xs text-slate-500 font-normal truncate">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="change-password-btn" onClick={() => setCpOpen(true)}>
              <KeyRound className="w-4 h-4 mr-2" /> Şifre Değiştir
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="business-settings-btn" onClick={() => setBsOpen(true)}>
              <Settings className="w-4 h-4 mr-2" /> İşletme Ayarları
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-rose-600">
              <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ChangePasswordDialog open={cpOpen} onOpenChange={setCpOpen} />
      <BusinessSettingsDialog open={bsOpen} onOpenChange={setBsOpen} />
    </header>
  );
};

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-slate-950 text-slate-100 flex-col z-40"
      >
        <SidebarBody />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-backdrop"
          />
          <aside
            data-testid="mobile-sidebar"
            className="lg:hidden fixed left-0 top-0 bottom-0 w-72 bg-slate-950 text-slate-100 flex flex-col z-50 animate-in slide-in-from-left duration-200"
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 w-8 h-8 rounded-md bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-white z-10"
              data-testid="close-sidebar-btn"
              aria-label="Kapat"
            >
              <X className="w-4 h-4" />
            </button>
            <SidebarBody onNavigate={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <div className="lg:pl-64">
        <Header onOpenSidebar={() => setMobileOpen(true)} />
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
